import { DakenService, Loader } from '@hub/shared'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './input-record-employment.component.scoped.scss'
import './input-record-employment.component.scss'

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  label?: any
  disabled?: boolean
  readOnly?: boolean
  errorSpan?: boolean
  loading?: boolean
  required?: boolean
  externalError?: any
  id?: string
  name?: string
  externalIcon?: any
  errorIcon?: any
  value?: string
  backgroundColor?: string
  requiredOnLoad?: boolean
  type?: 'text' | 'password'
  onChange?: (event: any) => any
  onValidate?: (event: any) => any
}

export interface State {
  error: string
  value: string
  validatedDocument: boolean
  loading?: boolean
}

export default ({
  label,
  disabled = false,
  readOnly = false,
  errorSpan = true,
  loading,
  requiredOnLoad = false,
  required,
  externalError,
  id,
  name,
  errorIcon = <i className="fa-regular fa-triangle-exclamation"></i>,
  externalIcon,
  value = '',
  backgroundColor = '',
  className = '',
  type = 'text',
  onChange,
  onValidate,
}: Props) => {
  const dakenService = useMemo(() => new DakenService(), [])

  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`

  const [state, setState] = useState<State>({
    value: value,
    error: '',
    validatedDocument: false,
    loading: false,
  })
  const { t } = useTranslation('inputRecordEmployment')

  const checkRecordEmployment = useCallback((recordEmployment: string) => {
    let total = 0
    let residual = 0
    let strResto = ''

    if (recordEmployment?.length !== 11) return false

    if (recordEmployment === '00000000000') return false

    for (let i = 0, ftap: any = '3298765432', resultado = 0; i <= 9; i++) {
      resultado = Number(recordEmployment.slice(i, i + 1)) * ftap.slice(i, i + 1)
      total = total + resultado
    }

    residual = total % 11
    if (residual !== 0) {
      residual = 11 - residual
    }

    if (residual === 10 || residual === 11) {
      strResto = residual.toString()
      residual = Number(strResto.slice(1, 2))
    }

    if (residual !== Number(recordEmployment.slice(10, 11))) {
      return false
    }

    if (recordEmployment.length > 0 && (recordEmployment.length > 11 || recordEmployment.length < 11)) {
      return false
    }

    return true
  }, [])

  const getBanksFieldCount = useCallback(
    async (value: string) => {
      try {
        setState({ ...state, loading: true })
        return await dakenService.getBanksFieldCount(value)
      } catch (err) {
        throw err
      } finally {
        setState({ ...state, loading: false })
      }
    },
    [state, dakenService]
  )

  const handleChange = useCallback(
    async (event: any) => {
      const value = event?.target?.value || (event?.length ? event : '')

      if (externalError) {
        state.error = ''
        state.validatedDocument = true
      } else if (!!required && (!value || value.length < 1)) {
        state.error = t('required')
        state.validatedDocument = false
      } else if (!checkRecordEmployment(value)) {
        state.error = t('invalidRecordEmployment')
        state.validatedDocument = false
      } else if (checkRecordEmployment(value)) {
        const response = await getBanksFieldCount(value)
        if (response === 1) {
          state.error = t('repeatRecordEmployment')
          state.validatedDocument = false
        } else if (response !== 0 && response !== 1) {
          state.error = t('invalidRecordEmployment')
          state.validatedDocument = false
        } else {
          state.error = ''
          state.validatedDocument = true
        }
      }

      state.value = value

      setState({ ...state })
      if (onValidate && typeof onValidate === 'function') {
        onValidate(!state.error && !externalError && state.validatedDocument)
      }
      if (onChange && typeof onChange === 'function') {
        onChange(value)
      }
    },
    [externalError, required, t, state, checkRecordEmployment, getBanksFieldCount, onValidate, onChange]
  )

  useEffect(() => {
    if (state.value !== value) {
      setState({ ...state, value: value })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value, value])

  return (
    <div className={`wrapper-all-inside-input ${className}`}>
      {label ? (
        <label className={'label ' + `${externalError || state.error ? 'error ' : ''}`} htmlFor={id || timestamp}>
          {label}
        </label>
      ) : null}
      <div className={'no-padding wrapper-input wrapper-input-squid ' + `${externalError || state.error ? 'error ' : ''}`}>
        <input
          className={
            'col input ' +
            `${readOnly ? 'readonly ' : ''}` +
            `${disabled ? 'disabled ' : ''}` +
            `${loading || state.loading ? 'loading ' : ''}` +
            `${externalError || state.error ? 'error ' : ''}` +
            `${externalIcon ? 'has-icon-external ' : ''}`
          }
          style={{
            backgroundColor: backgroundColor,
          }}
          id={id || timestamp}
          type={type}
          inputMode="numeric"
          maxLength={11}
          name={name || timestamp}
          placeholder={''}
          value={state.value}
          required={required}
          disabled={disabled}
          readOnly={readOnly || loading || state.loading}
          onChange={handleChange}
        />
      </div>
      {loading || state.loading ? <Loader size="small" className={`display-block loader-input ${!label ? 'no-label' : ''}`} /> : null}
      {(externalIcon && !loading) || !state.loading ? (
        <span className={'icon icon-external ' + `${!label ? 'no-label ' : ''}`}>{externalIcon}</span>
      ) : null}
      {errorSpan ? (
        <div className={'box-validation box-invalid show '}>
          {requiredOnLoad && !state.value && !state.error && !externalError ? (
            <div>
              <i className="fa-solid fa-diamond-exclamation" style={{ color: 'var(--gold)' }}></i>
              {t('globalPayments:emptyField')}
            </div>
          ) : null}
          {state.error || externalError ? errorIcon || <i className="fa-regular fa-triangle-exclamation"></i> : null}
          {externalError ? externalError : ''}
          {state.error && !externalError ? <span className={'message-error'} dangerouslySetInnerHTML={{ __html: state.error }} /> : ''}
        </div>
      ) : null}
    </div>
  )
}
